import React, { useState } from 'react';
import { AppBar, Drawer, DrawerHeader } from './components';
import Toolbar from '@mui/material/Toolbar';
import { Box, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Typography from '@mui/material/Typography';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { RootState } from '../../../redux/store';
import { typesRedux } from '../../../shared/types/typesRedux';
import { AuthMessages } from '../../../shared/messages/authMessages';
import { useAuthContext } from '../../../contexts/authContext';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Sidebar from './sidebar';
import variables from '../../../shared/styles/settings.module.scss';
import { useNavigate } from 'react-router-dom';
import { SIGNIN } from '../../../routes/paths';

interface Option {
    route?: string | undefined;
    label: string;
    icon: JSX.Element | string;
    subItems?: any[];
    permissions?: any[];
}

type Props = {
    modules: Option[];
};

function NavBar({ modules }: Props) {
    const dispatch: any = useDispatch();
    const navigate = useNavigate();
    const { logout, modeTheme } = useAuthContext();
    const { logUser } = useSelector((state: RootState) => state.general);
    const { name, surname, type } = logUser || {};
    const [open, setOpen] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { TITLE_LOGOUT, MESSAGE_LOGOUT } = AuthMessages.logout;
    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const modeThemeClick = () => {
        handleClose();
        dispatch({
            type: typesRedux.MODE_THEME,
            payload: modeTheme === 'darkMode' ? 'lightMode' : 'darkMode',
        });
    };

    const logoutClick = () => {
        handleClose();
        Swal.fire({
            title: TITLE_LOGOUT,
            text: MESSAGE_LOGOUT,
            icon: 'question',
            iconColor:
                modeTheme === 'darkMode'
                    ? variables.darkColor
                    : variables.primaryColor,
            showCancelButton: true,
            cancelButtonText: 'NO',
            allowOutsideClick: false,
            cancelButtonColor:
                modeTheme === 'darkMode'
                    ? variables.darkColor
                    : variables.primaryColor,
            confirmButtonText: 'SI',
            confirmButtonColor:
                modeTheme === 'darkMode'
                    ? variables.darkColor
                    : variables.primaryColor,
            customClass: {
                container: 'swal_wide_class',
            },
        }).then((response) => {
            if (response.isConfirmed) {
                logout();
                dispatch({
                    type: typesRedux.LOGOUT_LOGIN,
                    payload: '',
                });
                navigate(SIGNIN);
            }
        });
    };

    const validatePermission = (permissions: any) => {
        const data = permissions?.find((p: any) => p === type);
        return data !== undefined;
    };

    return (
        <>
            <AppBar position="fixed" open={open}>
                <Toolbar>
                    <IconButton
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={{
                            marginRight: 4,
                            ...(open && { display: 'none' }),
                        }}
                    >
                        <MenuIcon sx={{ color: 'primary.contrastText' }} />
                    </IconButton>
                    <Box
                        component="img"
                        src={
                            modeTheme === 'darkMode'
                                ? require('../../../assets/images/logos/previsora_black.png')
                                : require('../../../assets/images/logos/previsora.png')
                        }
                        sx={{
                            width: '120px',
                            height: '65px',
                        }}
                        alt="logo"
                    />
                    <Typography
                        variant="h6"
                        component="div"
                        sx={{
                            flexGrow: 1,
                            ml: 1,
                            fontSize: { lg: 25, md: 21, xs: 12 },
                            letterSpacing: {
                                lg: '.3rem',
                                md: '.2rem',
                                xs: '.1rem',
                            },
                            textDecoration: 'none',
                            display: {
                                xs: open ? 'none' : 'flex',
                                sm: 'flex',
                                md: 'flex',
                            },
                        }}
                    >
                        CRMWare Previsora
                    </Typography>
                    <div>
                        <IconButton
                            edge="end"
                            size="large"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleMenu}
                        >
                            <Typography
                                component="div"
                                sx={{
                                    flexGrow: 1,
                                    mr: 1,
                                    fontSize: { lg: 15, md: 12.5, xs: 10 },
                                    textDecoration: 'none',
                                    textTransform: 'uppercase',
                                    color: 'primary.contrastText',
                                }}
                            >
                                {name !== undefined
                                    ? name + ' ' + surname
                                    : 'Username'}
                            </Typography>
                            <AccountCircle
                                sx={{ color: 'primary.contrastText' }}
                                className="mx-2"
                            />
                            <Typography
                                component="div"
                                sx={{
                                    flexGrow: 1,
                                    ml: 1,
                                    fontSize: { lg: 15, md: 12.5, xs: 10 },
                                    textDecoration: 'none',
                                    textTransform: 'uppercase',
                                    color: 'primary.contrastText',
                                    display: {
                                        xs: open ? 'none' : 'flex',
                                        sm: 'flex',
                                        md: 'flex',
                                    },
                                }}
                            >
                                {type || 'Rol'}
                            </Typography>
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                        >
                            <MenuItem
                                onClick={modeThemeClick}
                                sx={{ color: 'primary.main' }}
                            >
                                {modeTheme === 'darkMode'
                                    ? 'Modo Claro'
                                    : 'Modo Oscuro'}
                            </MenuItem>
                            <MenuItem
                                onClick={logoutClick}
                                sx={{ color: 'primary.main' }}
                            >
                                Cerrar Sesión
                            </MenuItem>
                        </Menu>
                    </div>
                </Toolbar>
            </AppBar>
            <ClickAwayListener
                mouseEvent="onMouseDown"
                touchEvent="onTouchStart"
                onClickAway={() => open && handleDrawerClose()}
            >
                <Drawer
                    variant="permanent"
                    open={open}
                    onClose={(_, reason) => reason === 'backdropClick'}
                >
                    <DrawerHeader>
                        <IconButton onClick={handleDrawerClose}>
                            <ChevronLeftIcon
                                sx={{ color: 'primary.contrastText' }}
                            />
                        </IconButton>
                    </DrawerHeader>
                    <Divider />
                    <List>
                        {modules.map(
                            (
                                { route, label, icon, subItems, permissions },
                                i
                            ) => (
                                <div key={i}>
                                    {(!permissions ||
                                        validatePermission(permissions)) && (
                                        <Sidebar
                                            route={route}
                                            label={label}
                                            icon={icon}
                                            subItems={subItems}
                                            open={open}
                                            type={type}
                                        />
                                    )}
                                </div>
                            )
                        )}
                    </List>
                </Drawer>
            </ClickAwayListener>
        </>
    );
}

export default NavBar;
